import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { 
  XMarkIcon, 
  CreditCardIcon, 
  CheckIcon, 
  ShieldCheckIcon,
  KeyIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentCheckIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { 
  obtenerConfiguracionMercadoPago, 
  guardarConfiguracionMercadoPago 
} from '../api/backend_v2';

const MercadoPagoConfigModal = ({ isOpen, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [mpEnabled, setMpEnabled] = useState(false);
  const [mpPublicKey, setMpPublicKey] = useState('');
  const [mpAccessToken, setMpAccessToken] = useState('');
  const [hasTokenStored, setHasTokenStored] = useState(false);
  const [maskedToken, setMaskedToken] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');

  const [mostrarToken, setMostrarToken] = useState(false);
  const [copiadoWebhook, setCopiadoWebhook] = useState(false);

  useEffect(() => {
    if (isOpen) {
      cargarConfiguracion();
    }
  }, [isOpen]);

  const cargarConfiguracion = async () => {
    try {
      setLoading(true);
      setTestResult(null);
      const data = await obtenerConfiguracionMercadoPago();
      setMpEnabled(Boolean(data.mp_enabled));
      setMpPublicKey(data.mp_public_key || '');
      setHasTokenStored(Boolean(data.has_access_token));
      setMaskedToken(data.access_token_masked || '');
      setWebhookUrl(data.webhook_url || '');
      setMpAccessToken('');
    } catch (error) {
      console.error('Error cargando configuración de Mercado Pago:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'No se pudo cargar la configuración de Mercado Pago',
        confirmButtonColor: '#0284c7'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProbarConexion = async () => {
    try {
      setProbando(true);
      setTestResult(null);
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const tokenAuth = localStorage.getItem('token');
      
      const res = await fetch(`${API_URL}/api/auth/mi-optica/mercadopago/test-connection`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenAuth}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.message || 'Error de conexión' });
    } finally {
      setProbando(false);
    }
  };

  const handleCopiarWebhook = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl);
    setCopiadoWebhook(true);
    setTimeout(() => setCopiadoWebhook(false), 2500);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    try {
      setGuardando(true);
      const payload = {
        mp_enabled: mpEnabled,
        mp_public_key: mpPublicKey.trim(),
        mp_access_token: mpAccessToken.trim() ? mpAccessToken.trim() : null
      };

      await guardarConfiguracionMercadoPago(payload);

      Swal.fire({
        icon: 'success',
        title: '¡Guardado!',
        text: 'La configuración de Mercado Pago se guardó correctamente y las credenciales han sido cifradas.',
        confirmButtonColor: '#0284c7',
        timer: 2000,
        showConfirmButton: false
      });

      if (onSaved) onSaved();
      onClose();
    } catch (error) {
      console.error('Error guardando configuración:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error al Guardar',
        text: error.message || 'Ocurrió un error al guardar las credenciales',
        confirmButtonColor: '#0284c7'
      });
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 overflow-y-auto bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[92vh] my-auto">

        {/* Header Modal */}
        <div className="px-6 py-5 bg-gradient-to-r from-sky-600 via-cyan-600 to-blue-700 text-white flex items-center justify-between shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-md">
              <CreditCardIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-black tracking-tight flex items-center gap-2">
                Pasarela Mercado Pago
                <span className="text-[10px] bg-white/20 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Checkout Pro
                </span>
              </h2>
              <p className="text-xs text-sky-100 font-medium">
                Configurá los cobros online directos a la cuenta de tu óptica
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Contenido scrolleable */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 text-slate-700 dark:text-slate-200">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center gap-3 text-slate-400">
              <ArrowPathIcon className="w-8 h-8 animate-spin text-cyan-500" />
              <p className="text-sm font-semibold">Cargando credenciales de la óptica...</p>
            </div>
          ) : (
            <form id="form-mp" onSubmit={handleGuardar} className="space-y-6">
              
              {/* Switch de Habilitación */}
              <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                <div className="space-y-0.5">
                  <span className="text-sm font-bold block text-slate-900 dark:text-white flex items-center gap-2">
                    Habilitar Pagos por Mercado Pago
                    {mpEnabled ? (
                      <span className="text-[10px] font-black bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded-full uppercase">
                        Activo en Tienda
                      </span>
                    ) : (
                      <span className="text-[10px] font-black bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 rounded-full uppercase">
                        Inactivo
                      </span>
                    )}
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Al activar, los clientes podrán abonar con Tarjetas y Dinero en Cuenta en tu tienda web.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={mpEnabled}
                    onChange={(e) => setMpEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                </label>
              </div>

              {/* Public Key */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Public Key (Clave Pública)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={mpPublicKey}
                    onChange={(e) => setMpPublicKey(e.target.value)}
                    placeholder="APP_USR-xxxxxx-xxxxxx..."
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-sm font-mono text-slate-900 dark:text-slate-100 focus:border-cyan-500 focus:outline-none"
                  />
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Visible públicamente para iniciar los formularios de cobro seguros.
                </p>
              </div>

              {/* Access Token */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Access Token Privado
                  </label>
                  {hasTokenStored && (
                    <span className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <ShieldCheckIcon className="w-3.5 h-3.5" /> Token Almacenado y Cifrado
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    type={mostrarToken ? 'text' : 'password'}
                    value={mpAccessToken}
                    onChange={(e) => setMpAccessToken(e.target.value)}
                    placeholder={hasTokenStored ? `Dejar vacío para conservar (${maskedToken})` : 'APP_USR-xxxxxx...'}
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-3 pr-11 text-sm font-mono text-slate-900 dark:text-slate-100 focus:border-cyan-500 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarToken(!mostrarToken)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    {mostrarToken ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Se encripta con algoritmo AES-256 en nuestra base de datos. Si no querés cambiarlo, dejá este campo en blanco.
                </p>
              </div>

              {/* Botón Probar Credenciales y Resultado */}
              <div className="p-4 rounded-2xl bg-sky-50/60 dark:bg-sky-950/20 border border-sky-100 dark:border-sky-900/50 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <KeyIcon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                    <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                      Diagnóstico de Conexión MP
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleProbarConexion}
                    disabled={probando}
                    className="px-3 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
                  >
                    {probando ? (
                      <>
                        <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> Verificando...
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="w-3.5 h-3.5" /> Testear Token Actual
                      </>
                    )}
                  </button>
                </div>

                {testResult && (
                  <div className={`p-3 rounded-xl text-xs font-medium border flex items-start gap-2.5 ${
                    testResult.ok 
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300' 
                      : 'bg-rose-50 dark:bg-rose-950/40 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-300'
                  }`}>
                    {testResult.ok ? (
                      <CheckCircleIcon className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    ) : (
                      <ExclamationTriangleIcon className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    )}
                    <div className="space-y-0.5">
                      <p className="font-bold">{testResult.mensaje || (testResult.ok ? 'Token Válido' : 'Error en Validación')}</p>
                      {testResult.nickname && (
                        <p className="text-[11px] opacity-90">Cuenta vinculada: <strong>{testResult.nickname}</strong> (Modo Producción: {testResult.live_mode ? 'Sí' : 'Sandbox/Pruebas'})</p>
                      )}
                      {testResult.error && (
                        <p className="text-[11px]">{testResult.error}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* URL del Webhook (IPN) */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  URL de Notificaciones Webhook (IPN)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={webhookUrl}
                    className="w-full bg-slate-100 dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-xl p-3 text-xs font-mono text-slate-600 dark:text-slate-400 select-all"
                  />
                  <button
                    type="button"
                    onClick={handleCopiarWebhook}
                    className="px-4 py-3 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors shrink-0"
                  >
                    {copiadoWebhook ? (
                      <>
                        <ClipboardDocumentCheckIcon className="w-4 h-4 text-emerald-500" /> Copiado
                      </>
                    ) : (
                      <>
                        <ClipboardDocumentIcon className="w-4 h-4" /> Copiar
                      </>
                    )}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">
                  Pegá esta URL en la sección <strong>Webhooks</strong> del panel de desarrolladores de Mercado Pago para la confirmación automática de cobros y stock.
                </p>
              </div>

              {/* Guía Rápida */}
              <div className="p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 text-xs text-amber-900 dark:text-amber-300 space-y-2">
                <div className="flex items-center gap-1.5 font-bold">
                  <InformationCircleIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
                  <span>¿Dónde encuentro mis credenciales de Mercado Pago?</span>
                </div>
                <ol className="list-decimal list-inside space-y-1 text-[11px] text-amber-800 dark:text-amber-400">
                  <li>Ingresá a tu cuenta en <a href="https://www.mercadopago.com.ar/developers" target="_blank" rel="noreferrer" className="underline font-bold text-cyan-600 dark:text-cyan-400">mercadopago.com.ar/developers</a>.</li>
                  <li>Creá o seleccioná tu aplicación en <strong>Tus integraciones</strong>.</li>
                  <li>Andá a la pestaña <strong>Credenciales de Producción</strong>.</li>
                  <li>Copiá la <strong>Public Key</strong> y el <strong>Access Token</strong> y pegalos arriba.</li>
                </ol>
              </div>

            </form>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="form-mp"
            disabled={guardando || loading}
            className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 text-white text-xs font-bold flex items-center gap-2 shadow-lg shadow-cyan-600/30 transition-all"
          >
            {guardando ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" /> Guardando...
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" /> Guardar Credenciales
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

MercadoPagoConfigModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};
export default MercadoPagoConfigModal;
